/**
 * IgnitionFireButton — the Act 4 trigger.
 *
 * STATES:
 *   • idle      — pink-glow CTA pill: "FIRE THE WAVE →"
 *   • countdown — button shrinks, an SVG ring sweeps CLOSED counter-
 *                 clockwise around it, and the big 3-2-1 number
 *                 replaces the label. Hard-locked at 3 seconds total
 *                 (1s per number).
 *   • igniting  — the button itself dissolves into a pink burst; the
 *                 actual cinematic effects (PZ bloom, edge cascade,
 *                 curve-first-segment turning solid pink, pulse meter
 *                 materializing) live on RadialNetwork, not here.
 *
 * The button is a presentational primitive only. Orchestration of
 * phases is owned by BriefingCanvas, which passes `phase` + `countdownN`.
 */
import React from 'react';
import { Flame } from 'lucide-react';

const PINK = '#FF4D94';
const TEAL = '#00D4FF';

// Visual constants
const SIZE_IDLE       = 168;   // pill diameter (idle / hover)
const SIZE_COUNTDOWN  = 136;   // shrunk during 3-2-1
const RING_STROKE     = 4;

export default function IgnitionFireButton({
  phase = 'idle',         // 'idle' | 'countdown' | 'igniting'
  countdownN = null,      // 3 | 2 | 1 | null
  onFire,
}) {
  const isCountdown = phase === 'countdown';
  const isIgniting  = phase === 'igniting';
  const size = isCountdown || isIgniting ? SIZE_COUNTDOWN : SIZE_IDLE;
  const ringR = size / 2 - RING_STROKE;
  const circ  = 2 * Math.PI * ringR;

  // Progress = how much of the ring has SWEPT CLOSED.
  // 3 → 25% closed, 2 → 50% closed, 1 → 75% closed. The remaining 25%
  // closes when the ignite phase fires.
  let progress = 0;
  if (countdownN === 3) progress = 0.25;
  if (countdownN === 2) progress = 0.50;
  if (countdownN === 1) progress = 0.75;
  if (isIgniting)       progress = 1.0;

  // strokeDashoffset shrinks from `circ` (no ring) to `0` (full ring)
  const dashoffset = circ * (1 - progress);

  const label = isIgniting
    ? '🔥'
    : isCountdown
      ? String(countdownN ?? '')
      : null;

  return (
    <div
      data-testid="ignition-fire-button"
      data-phase={phase}
      data-countdown={countdownN ?? ''}
      className="relative"
      style={{
        width:  SIZE_IDLE,   // reserve idle size so layout doesn't jump
        height: SIZE_IDLE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Ring sweep — SVG circle whose dashoffset closes as the
          countdown ticks. Hidden in idle. */}
      {(isCountdown || isIgniting) && (
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="absolute pointer-events-none"
          style={{
            left:  (SIZE_IDLE - size) / 2,
            top:   (SIZE_IDLE - size) / 2,
            transition: 'width 280ms ease-out, height 280ms ease-out, left 280ms ease-out, top 280ms ease-out',
          }}
        >
          {/* Faint background track */}
          <circle
            cx={size / 2} cy={size / 2}
            r={ringR}
            fill="none"
            stroke={`${PINK}33`}
            strokeWidth={RING_STROKE}
          />
          {/* Sweeping closure */}
          <circle
            data-testid="ignition-ring-sweep"
            cx={size / 2} cy={size / 2}
            r={ringR}
            fill="none"
            stroke={PINK}
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={dashoffset}
            style={{
              transition: 'stroke-dashoffset 980ms cubic-bezier(0.4, 0, 0.2, 1)',
              transform:  `rotate(-90deg)`,
              transformOrigin: 'center',
              filter:     `drop-shadow(0 0 12px ${PINK})`,
            }}
          />
        </svg>
      )}

      {/* Button core */}
      <button
        data-testid="fire-the-wave-btn"
        onClick={phase === 'idle' ? onFire : undefined}
        disabled={phase !== 'idle'}
        className="relative rounded-full font-black flex items-center justify-center gap-2"
        style={{
          width:  size,
          height: size,
          background: isIgniting
            ? `radial-gradient(circle at center, ${PINK} 0%, ${PINK}aa 60%, transparent 90%)`
            : `radial-gradient(circle at 30% 30%, ${PINK}, ${PINK}cc 60%, ${PINK}88 100%)`,
          color: '#FFFFFF',
          fontSize: isCountdown ? 72 : 16,
          letterSpacing: isCountdown ? '0.02em' : '0.16em',
          textTransform: 'uppercase',
          border: `2px solid ${PINK}`,
          boxShadow: isIgniting
            ? `0 0 80px ${PINK}, 0 0 160px ${PINK}99`
            : `0 0 40px ${PINK}99, inset 0 0 24px ${PINK}66`,
          cursor: phase === 'idle' ? 'pointer' : 'default',
          transition: 'width 280ms ease-out, height 280ms ease-out, font-size 220ms ease-out, box-shadow 280ms ease-out, background 280ms ease-out',
          textShadow: isCountdown ? `0 0 24px ${PINK}` : `0 1px 4px rgba(0,0,0,0.5)`,
          fontFamily: 'inherit',
          animation: phase === 'idle'
            ? 'fireButtonIdlePulse 2400ms ease-in-out infinite'
            : isIgniting
              ? 'fireButtonIgnite 800ms ease-out forwards'
              : 'none',
        }}
      >
        {label != null ? (
          <span data-testid="ignition-label">{label}</span>
        ) : (
          <>
            <Flame className="h-5 w-5" strokeWidth={2.5} />
            <span>Fire the Wave</span>
          </>
        )}
      </button>

      <style>{`
        @keyframes fireButtonIdlePulse {
          0%, 100% { box-shadow: 0 0 40px ${PINK}99, inset 0 0 24px ${PINK}66; }
          50%      { box-shadow: 0 0 72px ${PINK}, inset 0 0 32px ${PINK}aa; }
        }
        @keyframes fireButtonIgnite {
          from { transform: scale(1); opacity: 1; }
          to   { transform: scale(1.4); opacity: 0; }
        }
      `}</style>

      {/* Helper caption — only visible idle */}
      {phase === 'idle' && (
        <p
          className="absolute"
          data-testid="fire-helper-caption"
          style={{
            top: SIZE_IDLE + 12,
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
            color: TEAL,
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            fontWeight: 700,
            opacity: 0.85,
          }}
        >
          3 · 2 · 1 to ignition
        </p>
      )}
    </div>
  );
}
